import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

type Props = {
    beforeUrl: string;
    afterUrl: string;
    beforeLabel?: string;
    afterLabel?: string;
    beforeAlt?: string;
    afterAlt?: string;
    /** KI-Kennzeichnung des Vorher-Bildes (aus `media.is_ai_generated`). */
    beforeAi?: boolean;
    /** KI-Kennzeichnung des Nachher-Bildes (aus `media.is_ai_generated`). */
    afterAi?: boolean;
    /** Container-Seitenverhältnis (default 3/2 — passt für Interior-Bilder). */
    aspectRatio?: string;
    /** Startposition in Prozent (0-100). */
    initial?: number;
};

/**
 * Vorher/Nachher-Slider — professionell gehärtet.
 *
 * - Beide Bilder werden IDENTISCH gerendert (`object-cover object-center` +
 *   feste `aspect-ratio`). Kein Verzerren, kein Squeeze — der Vergleich ist
 *   pixelgenau ausgerichtet.
 * - Divider + Handle bewegen sich via `transform: translateX` (Sub-Pixel-
 *   Rendering) statt via `left: %`.
 * - Beide Bilder sind eager geladen; der Divider verwendet nur den
 *   `clip-path`, sodass keine Layout-Recomputes stattfinden.
 * - Pointer-Events (kein separates touch/mouse Handling).
 *   `pointer-capture` auf pointerdown → konsistentes Move-Tracking bei drag.
 *   `touch-action: none` nur auf dem Handle-Bereich; Container erlaubt
 *   `pan-y` sodass Seiten-Scroll noch funktioniert.
 * - `ResizeObserver` triggert Re-Render nicht — State bleibt in Prozent,
 *   `transform` skaliert automatisch mit Container-Width.
 * - ARIA-`slider` + Keyboard: Left/Right = ±5 %, PageUp/PageDown = ±10 %,
 *   Home/End = 0/100 %.
 */
export default function BeforeAfterSlider({
    beforeUrl,
    afterUrl,
    beforeLabel = "Vorher",
    afterLabel = "Nachher",
    beforeAlt = "Vorher-Aufnahme",
    afterAlt = "Nachher-Aufnahme",
    beforeAi = false,
    afterAi = false,
    aspectRatio = "3 / 2",
    initial = 50,
}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<number>(Math.max(0, Math.min(100, initial)));
    const [dragging, setDragging] = useState(false);

    const setFromClientX = useCallback((clientX: number) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const rel = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(0, Math.min(100, rel)));
    }, []);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
        setFromClientX(e.clientX);
    }, [setFromClientX]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging) return;
        setFromClientX(e.clientX);
    }, [dragging, setFromClientX]);

    const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        setDragging(false);
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);

    const onKey = useCallback((e: React.KeyboardEvent) => {
        let handled = true;
        if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
        else if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
        else if (e.key === "PageDown") setPos((p) => Math.max(0, p - 10));
        else if (e.key === "PageUp") setPos((p) => Math.min(100, p + 10));
        else if (e.key === "Home") setPos(0);
        else if (e.key === "End") setPos(100);
        else handled = false;
        if (handled) e.preventDefault();
    }, []);

    // ResizeObserver — nur damit uns Zoom-Change bewusst wird (state bleibt in %,
    // transform skaliert mit — kein Neu-Positionieren nötig, aber wir triggern
    // re-render bei drastischen Änderungen um Anti-Aliasing sauber zu halten).
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => {
            // no-op: transform ist relativ zu 100% Container-Width, skaliert
            // automatisch. Wir touchen state trotzdem NICHT damit kein Flicker.
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const positionStyle: React.CSSProperties = { left: `${pos}%` };
    const positionTransition = dragging ? "none" : "left 200ms cubic-bezier(0.22, 1, 0.36, 1)";
    const clipStyle: React.CSSProperties = {
        clipPath: `inset(0 ${100 - pos}% 0 0)`,
    };
    const clipTransition = dragging ? "none" : "clip-path 200ms cubic-bezier(0.22, 1, 0.36, 1)";

    return (
        <div
            ref={containerRef}
            className="relative w-full overflow-hidden rounded-sm select-none"
            style={{
                aspectRatio,
                background: "hsl(var(--hf-surface))",
                border: "1px solid hsl(var(--hf-line))",
                touchAction: "pan-y",
                cursor: dragging ? "grabbing" : "ew-resize",
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            data-testid="before-after-slider"
        >
            {/* AFTER (Basis, Vollflächig) */}
            <img
                src={afterUrl}
                alt={afterAlt}
                draggable={false}
                loading="eager"
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{ objectPosition: "center" }}
                data-testid="ba-img-after"
            />
            {/* BEFORE — via clip-path bis pos% sichtbar */}
            <img
                src={beforeUrl}
                alt={beforeAlt}
                draggable={false}
                loading="eager"
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{
                    objectPosition: "center",
                    willChange: dragging ? "clip-path" : "auto",
                    transition: clipTransition,
                    ...clipStyle,
                }}
                data-testid="ba-img-before"
            />

            {/*
              * Label-Chips — folgen dem Slider (Variante A).
              *
              * VORHER-Label: rechte Kante 12px LINKS vom Divider.
              *   → right = calc((100 - pos)% + 12px)
              *   → das Label schwebt immer unmittelbar auf der sichtbaren
              *     Vorher-Seite, egal wo der User den Slider hält.
              * NACHHER-Label: linke Kante 12px RECHTS vom Divider.
              *   → left  = calc(pos% + 12px)
              *
              * Rand-Fälle: bei pos<12% bzw. pos>88% würde das jeweilige
              * Label die Container-Kante schneiden. Wir blenden es dann
              * sanft aus, statt es abzuschneiden oder außerhalb rendern
              * zu lassen.
              *
              * KEIN clipPath — führt zu abgeschnittenem Text ("VOR" / "HHER"),
              * wenn der Divider mitten durch das Label wandert.
              */}
            <span
                className="absolute top-3 px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase rounded-sm pointer-events-none whitespace-nowrap"
                style={{
                    right: `calc(${100 - pos}% + 12px)`,
                    background: "hsl(var(--hf-ink) / 0.78)",
                    color: "hsl(var(--hf-gold-soft))",
                    border: "1px solid hsl(var(--hf-gold) / 0.55)",
                    opacity: pos <= 12 ? 0 : (dragging ? 0.6 : 0.92),
                    transition: dragging
                        ? "opacity 120ms ease"
                        : "right 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease",
                    willChange: dragging ? "right, opacity" : "auto",
                }}
                data-testid="ba-label-before"
            >
                {beforeLabel}
            </span>
            <span
                className="absolute top-3 px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase rounded-sm pointer-events-none whitespace-nowrap"
                style={{
                    left: `calc(${pos}% + 12px)`,
                    background: "hsl(var(--hf-ink) / 0.78)",
                    color: "hsl(var(--hf-gold-soft))",
                    border: "1px solid hsl(var(--hf-gold) / 0.55)",
                    opacity: pos >= 88 ? 0 : (dragging ? 0.6 : 0.92),
                    transition: dragging
                        ? "opacity 120ms ease"
                        : "left 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease",
                    willChange: dragging ? "left, opacity" : "auto",
                }}
                data-testid="ba-label-after"
            >
                {afterLabel}
            </span>

            {/*
             * KI-Badges — sitzen jeweils an der äußeren Ecke ihrer Bildhälfte
             * (Vorher: oben-links, Nachher: oben-rechts) und faden analog zu
             * den Vorher/Nachher-Labels aus, wenn das jeweilige Bild fast
             * komplett vom Divider überdeckt wird. So bleibt das Badge immer
             * auf dem aktuell sichtbaren Bildinhalt.
             *
             * Design 1:1 identisch zu `<AiBadge/>` (dezenter dunkler Chip mit
             * Sparkles-Icon + „KI"), nur mit override-baren Positions- und
             * Fade-Styles.
             */}
            {beforeAi && (
                <span
                    className="pointer-events-none absolute z-10 inline-flex items-center gap-1 rounded-full backdrop-blur-sm"
                    aria-label="Vorher-Bild ist KI-generiert"
                    data-testid="ba-ai-badge-before"
                    style={{
                        top: 12,
                        left: 12,
                        background: "rgba(23, 22, 19, 0.7)",
                        color: "#D4A574",
                        padding: "2px 5px",
                        fontSize: 8,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        border: "1px solid rgba(215, 170, 100, 0.25)",
                        boxShadow: "0 3px 8px -3px rgba(0, 0, 0, 0.35)",
                        opacity: pos <= 12 ? 0 : (dragging ? 0.75 : 1),
                        transition: dragging
                            ? "opacity 120ms ease"
                            : "opacity 200ms ease",
                    }}
                >
                    <Sparkles size={8} strokeWidth={2} aria-hidden style={{ color: "#EBD5A0" }} />
                    KI
                </span>
            )}
            {afterAi && (
                <span
                    className="pointer-events-none absolute z-10 inline-flex items-center gap-1 rounded-full backdrop-blur-sm"
                    aria-label="Nachher-Bild ist KI-generiert"
                    data-testid="ba-ai-badge-after"
                    style={{
                        top: 12,
                        right: 12,
                        background: "rgba(23, 22, 19, 0.7)",
                        color: "#D4A574",
                        padding: "2px 5px",
                        fontSize: 8,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        border: "1px solid rgba(215, 170, 100, 0.25)",
                        boxShadow: "0 3px 8px -3px rgba(0, 0, 0, 0.35)",
                        opacity: pos >= 88 ? 0 : (dragging ? 0.75 : 1),
                        transition: dragging
                            ? "opacity 120ms ease"
                            : "opacity 200ms ease",
                    }}
                >
                    <Sparkles size={8} strokeWidth={2} aria-hidden style={{ color: "#EBD5A0" }} />
                    KI
                </span>
            )}

            {/* Divider-Linie — via left:% (Container-relativ) + transform translateX(-50%) */}
            <div
                aria-hidden
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                    ...positionStyle,
                    width: 2,
                    transform: "translateX(-50%)",
                    transition: positionTransition,
                    background: "hsl(var(--hf-gold))",
                    boxShadow: "0 0 0 1px hsl(var(--hf-ink) / 0.15)",
                    willChange: dragging ? "left" : "auto",
                }}
            />

            {/* Handle — gleiche `left:%` Position wie Divider, zentriert per translate(-50%,-50%) */}
            <div
                role="slider"
                aria-label="Vorher / Nachher Vergleich"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pos)}
                tabIndex={0}
                onKeyDown={onKey}
                className="absolute rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                    ...positionStyle,
                    top: "50%",
                    width: 44,
                    height: 44,
                    transform: "translate(-50%, -50%)",
                    transition: positionTransition,
                    background: "hsl(var(--hf-gold))",
                    border: "2px solid hsl(var(--hf-bg))",
                    boxShadow: "0 4px 14px hsl(var(--hf-ink) / 0.35)",
                    touchAction: "none",
                    cursor: dragging ? "grabbing" : "ew-resize",
                    willChange: dragging ? "left" : "auto",
                }}
                data-testid="ba-handle"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--hf-ink))" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 6 3 12 9 18" />
                    <polyline points="15 6 21 12 15 18" />
                </svg>
            </div>
        </div>
    );
}
